/**
 * pii-redactor.ts
 *
 * ISO/IEC 27701:2019 Privacy Information Management System (PIMS) automated
 * PII redaction filters for telemetry, audit sinks, and logging (Sprint AUTH-01).
 */

export class PiiRedactor {
  // Common PII regular expressions
  private static readonly EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  private static readonly PHONE_REGEX = /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;
  private static readonly IP_REGEX = /\b(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\b/g;
  private static readonly SSN_REGEX = /\b\d{3}-\d{2}-\d{4}\b/g;
  private static readonly SECRET_REGEX = /(?:bearer\s+[a-zA-Z0-9._~+/-]+|0x[a-fA-F0-9]{64}|ed25519_sk_[a-zA-Z0-9]+)/gi;

  /**
   * Sanitizes a single string value by replacing sensitive PII patterns.
   */
  static redactString(value: string): string {
    if (!value || typeof value !== 'string') return value;

    return value
      .replace(PiiRedactor.SECRET_REGEX, '[REDACTED_SECRET]')
      .replace(PiiRedactor.SSN_REGEX, '[REDACTED_SENSITIVE_ID]')
      .replace(PiiRedactor.EMAIL_REGEX, '[REDACTED_EMAIL]')
      .replace(PiiRedactor.PHONE_REGEX, '[REDACTED_PHONE]')
      .replace(PiiRedactor.IP_REGEX, '[REDACTED_NETWORK_ADDR]');
  }

  /**
   * Recursively traverses and sanitizes objects, arrays, and nested structures.
   */
  static redactDeep<T>(input: T): T {
    if (input === null || input === undefined) {
      return input;
    }

    if (typeof input === 'string') {
      return PiiRedactor.redactString(input) as unknown as T;
    }

    if (Array.isArray(input)) {
      return input.map((item) => PiiRedactor.redactDeep(item)) as unknown as T;
    }

    if (typeof input === 'object') {
      const sanitized: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(input)) {
        // Redact known sensitive field names explicitly
        const lowerKey = key.toLowerCase();
        if (
          lowerKey.includes('password') ||
          lowerKey.includes('privatekey') ||
          lowerKey.includes('secret') ||
          lowerKey.includes('token')
        ) {
          sanitized[key] = '[REDACTED_SECRET]';
        } else {
          sanitized[key] = PiiRedactor.redactDeep(val);
        }
      }
      return sanitized as unknown as T;
    }

    return input;
  }
}

export const redactPiiDeep = PiiRedactor.redactDeep;
export const redactPiiString = PiiRedactor.redactString;
